import { TicketSalesResultType } from '@api';
import { formatNumber, LinkableEntity, ShowTableCell } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import {
  ShowDateTableCell,
  TicketSalesSellableCell,
  TicketSalesSoldPercentCell,
  TicketSalesTimelineCell,
  TicketSalesTourRepsCell,
} from './components';

const TRANS_PATH = 'TABLE.COLUMNS';

type UseTicketSalesTableColumnsReturnType = {
  columns: DataTableColumn<TicketSalesResultType>[];
};

type UseTicketSalesTableColumnsPropsType = {
  onUpdateSellable: (ticketSalesId: string, ticketsSellable: number) => void;
};

export const useTicketSalesTableColumns = ({
  onUpdateSellable,
}: UseTicketSalesTableColumnsPropsType): UseTicketSalesTableColumnsReturnType => {
  const { t } = useTranslation('ticketSales');
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<TicketSalesResultType>[] = [
    {
      accessor: 'date',
      sortable: true,
      width: 110,
      title: t(`${TRANS_PATH}.DATE`),
      render: ({ showWithTour: { showDate }, showDateInfo }) => (
        <ShowDateTableCell showDate={showDate} showDateInfo={showDateInfo} />
      ),
    },
    {
      accessor: 'show',
      sortable: true,
      width: 300,
      title: t(`${TRANS_PATH}.SHOW`),
      render: ({ showWithTour }) => <ShowTableCell show={showWithTour} />,
    },
    {
      accessor: 'copro',
      sortable: true,
      width: 200,
      title: t(`${TRANS_PATH}.COPRO`),
      render: ({ coPro }) =>
        coPro ? <LinkableEntity linkableEntity={coPro} /> : noDataPlaceholder,
    },
    {
      accessor: 'buyer',
      sortable: true,
      width: 200,
      title: t(`${TRANS_PATH}.BUYER`),
      render: ({ buyer }) =>
        buyer ? <LinkableEntity linkableEntity={buyer} /> : noDataPlaceholder,
    },
    {
      accessor: 'tourreps',
      title: t(`${TRANS_PATH}.TOURREPS`),
      width: 260,
      render: ({ tourProd, tourRep, tourAcc }) => (
        <TicketSalesTourRepsCell
          tourProd={tourProd}
          tourRep={tourRep}
          tourAcc={tourAcc}
        />
      ),
    },
    {
      accessor: 'sold',
      title: t(`${TRANS_PATH}.SOLD`),
      render: ({ ticketsSold }) => formatNumber(ticketsSold),
    },
    {
      accessor: 'sellable',
      title: t(`${TRANS_PATH}.SELLABLE`),
      width: 120,
      render: ({ ticketSalesId, ticketsSellable }) => (
        <TicketSalesSellableCell
          ticketSalesId={ticketSalesId}
          ticketsSellable={ticketsSellable}
          onUpdateSellable={onUpdateSellable}
        />
      ),
    },
    {
      accessor: 'soldPercent',
      title: t(`${TRANS_PATH}.SOLD_PERCENT`),
      width: 100,
      render: ({ ticketSalesId, ticketsSold, ticketsSellable }) => (
        <TicketSalesSoldPercentCell
          ticketSalesId={ticketSalesId}
          ticketsSold={ticketsSold}
          ticketsSellable={ticketsSellable}
          noDataPlaceholder={noDataPlaceholder}
        />
      ),
    },
    {
      accessor: 'salesTimeline',
      title: t(`${TRANS_PATH}.SALES_TIMELINE`),
      render: ({ salesTimeline }) => (
        <TicketSalesTimelineCell
          salesTimeline={salesTimeline}
          noDataPlaceholder={noDataPlaceholder}
        />
      ),
    },
  ];

  return { columns };
};
